// Google-identity auth for backend API calls — REPLACES the X-Admin-Key wrapper.
//
// Installs a global fetch wrapper + axios interceptor that attaches the operator's
// Google ID token as `Authorization: Bearer <jwt>` on the flash-chatbot backend
// origins AND the CRIO proxy.
//
// The proxy was deliberately excluded until 2026-08-24, on the reasoning that it
// "stays credential-free and keeps using its own session tokens". That was only
// half true: the pasted CRIO session covers the internal/* schedule endpoints, but
// the proxy ALSO holds a long-lived CRIO bearer token and relays appointment
// writes with it — while being allUsers-invokable and performing no auth at all.
// Sending our identity is what lets it stop being open. The pasted-session
// mechanism is untouched; this is an outer layer, not a replacement.
//
// Expiry model (deliberately simple + robust): Google ID tokens last ~1h. While a
// stored token is valid it's attached to every gated request. When it expires — or a
// gated request returns 401 — we clear the identity and broadcast
// `google-auth-expired`, which drops the app back to the Google login gate for a
// one-click re-login. No shared key, no invisible-refresh edge cases. Works
// identically for any @delricht.com user on any device, anywhere.
//
// "View as" (auth/viewAs.ts): while a target is set for this tab, `X-View-As` rides
// next to Authorization on the IDENTITY origins only — never the CRIO proxy, and never
// on the excluded per-user-credential paths. The server substitutes the operator; a
// 403 whose detail is one of its two refusals means the target is stale (grant gone,
// person off the roster), so the target is dropped and the tab reloads once as the
// real person. The header is never overwritten if a caller set it itself.

import axios from 'axios';
import { appConfig } from '@/utils/config';
import { getStoredToken, isExpired, clearIdentity } from './googleToken';
import {
  VIEW_AS_HEADER, dropViewAsAfterRefusal, getViewAs, isExcludedPath, isViewAsRefusal,
} from './viewAs';

export const AUTH_EXPIRED_EVENT = 'google-auth-expired';

// The backend origins and the CRIO proxy. Deduplicated, because flashBotApi and
// geminiBotApi are the same host today and a duplicate would be harmless but
// misleading to read.
const gatedOrigins = Array.from(
  new Set(
    [appConfig.flashBotApi, appConfig.geminiBotApi, appConfig.crioApiEndpoint]
      .filter(Boolean)
      .map((u) => new URL(u).origin)
  )
);

// Origins whose 401 means the OPERATOR'S identity is bad, so the login gate should
// reappear. The CRIO proxy is gated (it receives the token) but deliberately NOT
// here: `/crio/auth/login` answers 401 for a wrong CRIO password, and treating that
// as an expired Google session would sign the coordinator out of the dashboard for
// mistyping an unrelated credential.
const identityOrigins = Array.from(
  new Set(
    [appConfig.flashBotApi, appConfig.geminiBotApi]
      .filter(Boolean)
      .map((u) => new URL(u).origin)
  )
);

/** The absolute URL of whatever fetch was handed, or null when it cannot be read. */
function urlOf(input: RequestInfo | URL | string): URL | null {
  try {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.href
        : (input as Request).url;
    return new URL(url, window.location.href);
  } catch {
    return null;
  }
}

function isIdentityUrl(input: RequestInfo | URL | string): boolean {
  const u = urlOf(input);
  return !!u && identityOrigins.includes(u.origin);
}

function isGatedUrl(input: RequestInfo | URL | string): boolean {
  const u = urlOf(input);
  return !!u && gatedOrigins.includes(u.origin);
}

/** The view-as target email to send for this URL, or null (not viewing / proxy / excluded path). */
function viewAsFor(input: RequestInfo | URL | string): string | null {
  const u = urlOf(input);
  if (!u || !identityOrigins.includes(u.origin) || isExcludedPath(u.pathname)) return null;
  return getViewAs()?.email ?? null;
}

/** The stored token if it's present and unexpired, else null. */
function validToken(): string | null {
  const token = getStoredToken();
  return token && !isExpired(token) ? token : null;
}

/** Clear identity and signal the app to show the login gate (one-click re-login). */
function onAuthFailed(): void {
  clearIdentity();
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

/** A 403 on a request that carried X-View-As: if it is the server refusing the view, end it. */
function onViewAsRefused(detail: unknown): void {
  if (isViewAsRefusal(detail)) dropViewAsAfterRefusal();
}

/** Install the global fetch wrapper + axios interceptors. Call ONCE at bootstrap. */
export function installGoogleApiAuth(): void {
  // --- fetch wrapper -------------------------------------------------------
  const realFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isGatedUrl(input)) return realFetch(input, init);

    const token = validToken();
    const headers = new Headers(
      init?.headers || (input instanceof Request ? input.headers : undefined)
    );
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    const viewAs = viewAsFor(input);
    if (viewAs && !headers.has(VIEW_AS_HEADER)) headers.set(VIEW_AS_HEADER, viewAs);

    const res = await realFetch(input, { ...init, headers });
    if (res.status === 401 && isIdentityUrl(input)) onAuthFailed();
    if (res.status === 403 && headers.has(VIEW_AS_HEADER) && isIdentityUrl(input)) {
      // Read a CLONE so the caller still gets the body; never let this read break their request.
      let detail: unknown;
      try { detail = (await res.clone().json())?.detail; } catch { detail = undefined; }
      onViewAsRefused(detail);
    }
    return res;
  };

  // --- axios interceptors ---------------------------------------------------
  axios.interceptors.request.use((config) => {
    const full = (config.baseURL || '') + (config.url || '');
    if (isGatedUrl(full)) {
      const token = validToken();
      if (token) {
        config.headers = Object.assign(config.headers || {}, { Authorization: `Bearer ${token}` });
      }
      const viewAs = viewAsFor(full);
      if (viewAs && !config.headers?.[VIEW_AS_HEADER]) {
        config.headers = Object.assign(config.headers || {}, { [VIEW_AS_HEADER]: viewAs });
      }
    }
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const cfg = error?.config;
      const full = cfg ? (cfg.baseURL || '') + (cfg.url || '') : '';
      const status = error?.response?.status;
      if (status === 401 && isIdentityUrl(full)) onAuthFailed();
      if (status === 403 && cfg?.headers?.[VIEW_AS_HEADER] && isIdentityUrl(full)) {
        onViewAsRefused(error?.response?.data?.detail);
      }
      return Promise.reject(error);
    }
  );
}
